// ============================================================================
// patientCardSummary.ts — ПРЕЗЕНТАЦИОННЫЕ СВОДКИ карты пациента (чистые
// функции, без SDK): что показать врачу в «Основном», что считать активным,
// как честно подвести итог импорта CSV.
//
// Модуль намеренно не зависит от сети и хранилищ: его проверяют юнит-тестами
// (tests/medic/patientCardSummary.test.ts), а экраны используют как SSoT.
// ============================================================================

import type { PatientRecordCollection, PatientRecordItem } from '@/components/medic/patientCardRecordsData';

/** Максимальный размер CSV с анализами (как на сервере). */
export const LAB_FILE_MAX_BYTES = 20 * 1024 * 1024;

export type RecordLike = Pick<PatientRecordItem, 'statusCode' | 'title' | 'date'>;

export type CollectionLike<T extends RecordLike = RecordLike> = {
    state: PatientRecordCollection['state'];
    items: T[];
};

/** Количество записей: null — раздел недоступен (не путать с «0 записей»). */
export function collectionCount<T extends RecordLike>(collection: CollectionLike<T>): number | null {
    return collection.state === 'ready' ? collection.items.length : null;
}

/** Записи с указанными статусами (по умолчанию — активные). */
export function itemsWithStatus<T extends RecordLike>(
    collection: CollectionLike<T>,
    statuses: string[] = ['active'],
): T[] {
    if (collection.state !== 'ready') return [];
    const wanted = new Set(statuses.map((status) => status.toLowerCase()));
    return collection.items.filter((item) => wanted.has(String(item.statusCode || '').toLowerCase()));
}

/** Самая свежая запись: данные карты приходят уже отсортированными по дате. */
export function latestItem<T extends RecordLike>(collection: CollectionLike<T>): T | null {
    if (collection.state !== 'ready') return null;
    return collection.items[0] || null;
}

export interface PatientRecordsLike {
    careTeam: CollectionLike<PatientRecordItem>;
    referrals: CollectionLike<PatientRecordItem>;
    labs: CollectionLike<PatientRecordItem>;
    diagnoses: CollectionLike<PatientRecordItem>;
    prescriptions: CollectionLike<PatientRecordItem>;
    allergies: CollectionLike<PatientRecordItem>;
    measurements: CollectionLike<PatientRecordItem>;
}

export interface PatientClinicalSummary {
    activeTeam: PatientRecordItem[];
    attending: PatientRecordItem | null;
    activeDiagnoses: PatientRecordItem[];
    activePrescriptions: PatientRecordItem[];
    activeAllergies: PatientRecordItem[];
    latestLab: PatientRecordItem | null;
    latestMeasurement: PatientRecordItem | null;
}

/**
 * Сводка «Основного» экрана: только то, что врач должен увидеть сразу —
 * действующие диагнозы, назначения и аллергии, последний анализ/измерение,
 * активная команда. Никаких копий данных из шапки.
 */
export function summarizeRecords(records: PatientRecordsLike): PatientClinicalSummary {
    const activeTeam = itemsWithStatus<PatientRecordItem>(records.careTeam, ['active']);
    return {
        activeTeam,
        attending: activeTeam.find((entry) => String(entry.roleCode || '').toLowerCase() === 'attending') || null,
        activeDiagnoses: itemsWithStatus<PatientRecordItem>(records.diagnoses, ['active']),
        activePrescriptions: itemsWithStatus<PatientRecordItem>(records.prescriptions, ['active']),
        activeAllergies: itemsWithStatus<PatientRecordItem>(records.allergies, ['active']),
        latestLab: latestItem<PatientRecordItem>(records.labs),
        latestMeasurement: latestItem<PatientRecordItem>(records.measurements),
    };
}

export interface CsvProblem {
    line: number;
}

export interface ImportSummary {
    imported: number;
    skipped: number;
    /** Ошибки конкретных строк файла (line > 0). */
    rowErrors: number;
    /** Ошибки всего файла (line <= 0). */
    fileErrors: number;
    /** Всего сообщений об ошибках. */
    problems: number;
    /** Есть ли что показывать в результате (создан документ). */
    hasData: boolean;
}

/**
 * Честный итог импорта. Серверный `skipped` заполняется только когда не
 * импортировано ни одной строки, поэтому «пропущено» в интерфейсе считаем по
 * ошибкам строк, а не по этому полю — иначе при частичном импорте рядом с
 * ошибкой показывалось бы «пропущено: 0».
 */
export function summarizeImport(
    imported: number,
    skipped: number,
    errors: CsvProblem[],
): ImportSummary {
    const safeImported = Math.max(0, Number(imported) || 0);
    const safeSkipped = Math.max(0, Number(skipped) || 0);
    let rowErrors = 0;
    let fileErrors = 0;
    for (const error of errors) {
        if (Number(error.line) > 0) rowErrors += 1;
        else fileErrors += 1;
    }
    return {
        imported: safeImported,
        skipped: safeSkipped,
        rowErrors,
        fileErrors,
        problems: errors.length,
        hasData: safeImported > 0,
    };
}

/** Проверка выбранного файла ДО отправки на сервер (явные сообщения). */
export function validateLabFile(file: { name: string; size: number }): string {
    const name = String(file.name || '').trim();
    if (!name) return 'Выберите файл CSV.';
    if (!/\.csv$/i.test(name)) return 'Нужен файл CSV (расширение .csv).';
    if (file.size <= 0) return 'Файл пуст — выберите выгрузку с данными.';
    if (file.size > LAB_FILE_MAX_BYTES) {
        return 'Файл больше 20 МБ — разделите выгрузку на части.';
    }
    return '';
}
